import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, catchError, delay, EMPTY, mergeMap, Observable, of, retry, tap, throwError } from "rxjs";
import { AccountCategory, AccountCategoryResponse } from "src/app/models/api/accountManagement.model";
import { AccountManagementHttpService } from "../../httpServices/accountManagementHttpService/account-management-http-service.service";
import { UserService } from "../user/user.service";
import { Router } from "@angular/router";
import { NotificationsService } from "../notifications/notifications.service";

@Injectable({
    providedIn: "root",
})
export class AccountService {
    private _accountCategory$ = new BehaviorSubject<AccountCategory | null>(null);

    private _accountHttpService = inject(AccountManagementHttpService);
    private _userService = inject(UserService);
    private _router = inject(Router);
    private _notificationService = inject(NotificationsService);

    public getAccountCategory$(): Observable<AccountCategory | null> {
        return this._accountCategory$.asObservable();
    }

    public setAccountCategory(accountDetails: AccountCategory | null): void {
        this._accountCategory$.next(accountDetails);
    }

    public getAccountDetails$(): Observable<AccountCategoryResponse> {
        const maxRetryAttempts = 3;

        return this._accountHttpService.getAccountDetails$().pipe(
            mergeMap((resp) => {
                if (resp.category === AccountCategory.inactive) {
                    this._logout();

                    return EMPTY;
                }

                return of(resp);
            }),
            tap((resp) => this.setAccountCategory(resp.category ?? null)),
            retry({
                count: maxRetryAttempts - 1,
                delay: (error, retryCount) => {
                    if (error.status >= 400) {
                        if (retryCount < maxRetryAttempts - 1) {
                            return of(error).pipe(delay(1000));
                        }

                        this._notificationService.error("Account category is not provided");
                        this.setAccountCategory(null);
                        this._logout();

                        return throwError(() => error);
                    }

                    return throwError(() => error);
                },
            }),
            catchError(() => EMPTY),
        );
    }

    public accountHasFullAccess(accountCategory: AccountCategory | null): boolean {
        return accountCategory === AccountCategory.internal || accountCategory === AccountCategory.paid || accountCategory === AccountCategory.zenity;
    }

    private _logout(): void {
        this._userService.logout();
        this._router.navigateByUrl("/login");
    }
}
